import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import useSWR, { mutate as swrMutate } from "swr";
import { api, downloadFile } from "@/lib/app";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Gear, DownloadSimple, CloudArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import StoreFormDialog from "@/components/StoreFormDialog";
import DataImport from "@/components/DataImport"; // used by StoreFormDialog too, imported here for the empty-state guidance link
import Overview from "@/components/Overview";
import HeatmapCalendar from "@/components/HeatmapCalendar";
import WeekdayCompare from "@/components/WeekdayCompare";
import HourlyBreakdown from "@/components/HourlyBreakdown";
import Efficiency from "@/components/Efficiency";
import AiReport from "@/components/AiReport";
import Notes from "@/components/Notes";
import PeriodCompare from "@/components/PeriodCompare";
import Anomalies from "@/components/Anomalies";
import Forecast from "@/components/Forecast";
import Predikcia from "@/components/Predikcia";
import WhatIf from "@/components/WhatIf";
import Highlights from "@/components/Highlights";
import PurchasesByHour from "@/components/PurchasesByHour";

const fetcher = (url) => api.get(url).then((r) => r.data);

const TABS = [
  { id: "overview", label: "Prehľad" },
  { id: "heatmap", label: "Výkonnosť" },
  { id: "weekday", label: "Dni v týždni" },
  { id: "hourly", label: "Hodinový profil" },
  { id: "customers", label: "Počet nákupov" },
  { id: "efficiency", label: "Efektívnosť" },
  { id: "anomalies", label: "Anomálie" },
  { id: "compare", label: "Porovnanie období" },
  { id: "forecast", label: "Plán personálu" },
  { id: "predikcia", label: "Predikcia" },
  { id: "summary", label: "Zhrnutie" },
];

export default function StoreDetail() {
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lockedIdp, tidoContext } = useAuth();
  const { data: store } = useSWR(`/stores/${storeId}`, fetcher);
  const { data: range } = useSWR(`/stores/${storeId}/data-range`, fetcher);
  const { data: highlightsList = [] } = useSWR(`/stores/${storeId}/highlights`, fetcher);
  const [tab, setTab] = useState("import");
  const [editOpen, setEditOpen] = useState(false);
  const [resyncBusy, setResyncBusy] = useState(false);

  const handleFullResync = async () => {
    if (!lockedIdp) return;
    const ok = window.confirm(
      "Vymaže všetky lokálne dáta pre toto stredisko a stiahne kompletnú históriu " +
      "z TiDo znova (môže trvať 1-2 minúty). Pokračovať?"
    );
    if (!ok) return;
    setResyncBusy(true);
    try {
      // 1) Clear existing data
      await api.delete(`/stores/${storeId}/data`);
      // 2) Init chunked re-sync with force_all
      const rok = tidoContext?.rok || new Date().getFullYear();
      const mesiac = tidoContext?.mesiac || (new Date().getMonth() + 1);
      const initBody = {
        center_id: parseInt(lockedIdp, 10),
        rok: parseInt(rok, 10),
        mesiac: parseInt(mesiac, 10),
        store_id: storeId,
        force_all: true,
      };
      const { data: init } = await api.post("/tido/uvitanie/init", initBody);
      const monthsToFetch = init.months.filter((m) => m.force);
      const lastIdx = monthsToFetch.length - 1;
      // 3) Fetch each month sequentially (no proxy timeout)
      for (let i = 0; i < monthsToFetch.length; i++) {
        const mm = monthsToFetch[i];
        await api.post("/tido/uvitanie/month", {
          domain: init.domain,
          store_id: init.store_id,
          center_id: parseInt(lockedIdp, 10),
          year: mm.year,
          month: mm.month,
          update_store_hp: i === lastIdx,
        });
      }
      toast.success(`Znovu-stiahnuté: ${monthsToFetch.length} mesiacov`);
      window.location.reload();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((x) => x?.msg || JSON.stringify(x)).join("; ")
        : (detail ? JSON.stringify(detail) : (e.message || "Znovu-stiahnutie zlyhalo"));
      toast.error(msg);
      setResyncBusy(false);
    }
  };

  // Auto-open config edit when ?editConfig=1 is set (from Smart Import flow)
  useEffect(() => {
    if (searchParams.get("editConfig") === "1") {
      setEditOpen(true);
      searchParams.delete("editConfig");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh TiDo data on store detail mount (once per browser session per store).
  // Silently fetches records from the last known day → today so charts always
  // show fresh data. No blocking, no confirm dialog — just a subtle toast.
  useEffect(() => {
    if (!store || !store.tido_center_id) return;
    const key = `tido_refresh_${storeId}`;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(key)) return;
    try { sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.post(`/tido/refresh/${storeId}`);
        const hasNewDay = data?.last_date_after && data?.last_date_before &&
                           data.last_date_after > data.last_date_before;
        if (hasNewDay) {
          const daysAdded = (new Date(data.last_date_after) - new Date(data.last_date_before)) / 86400000;
          toast.success(
            `Aktualizované z TiDo: +${Math.round(daysAdded)} ${daysAdded === 1 ? "deň" : daysAdded < 5 ? "dni" : "dní"} (do ${data.last_date_after})`
          );
          swrMutate(`/stores/${storeId}/data-range`);
          swrMutate(`/stores/${storeId}/plans/range`);
        }
      } catch (e) {
        // Silent — don't nag users if the auto-refresh fails; they can still
        // trigger a manual re-sync from the store detail header.
        const detail = e.response?.data?.detail;
        console.warn("Auto-refresh z TiDo zlyhal:", detail || e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // initialize period from URL params first, else from data range
  useEffect(() => {
    const urlFrom = searchParams.get("period_from");
    const urlTo = searchParams.get("period_to");
    if (urlFrom && !from) setFrom(urlFrom);
    else if (range?.min && !from) setFrom(range.min);
    if (urlTo && !to) setTo(urlTo);
    else if (range?.max && !to) setTo(range.max);
  }, [range, from, to, searchParams]);

  // auto-switch to overview when data exists (import tab is now inside the config dialog)
  useEffect(() => {
    if (range?.count > 0 && tab !== "overview" && TABS.every((t) => t.id !== tab)) setTab("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.count]);

  if (!store) return <div className="max-w-[1400px] mx-auto px-6 py-8 text-[var(--text-secondary)]">Načítavam...</div>;

  const hasData = range?.count > 0;
  const period = { from, to };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      {!lockedIdp && (
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4" data-testid="back-link">
          <ArrowLeft size={14} /> Späť na strediská
        </Link>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="label-eyebrow mb-1">Stredisko</p>
          <h1 className="font-display text-4xl font-bold tracking-tight tabular">{store.code}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {(store.name || "").replace(/\s*-\s*\d{1,2}\.\d{1,2}\.\d{4}.*$/, "").trim() || store.name}
          </p>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Požadovaný výkon <span className="tabular text-[var(--text-primary)]">{store.required_hourly_performance} €/os.hod</span>
            {hasData && (
              <>
                {" · "}
                Dáta:{" "}
                <span className="tabular text-[var(--text-primary)]">
                  {range.min} → {range.max}
                </span>{" "}
                ({range.days} dní, {range.count} záznamov)
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lockedIdp && (
            <button
              onClick={handleFullResync}
              disabled={resyncBusy}
              className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="tido-resync-btn"
              title="Vymazať a stiahnuť celú históriu z TiDo znova"
            >
              <CloudArrowDown size={16} /> {resyncBusy ? "Sťahujem…" : "Znovu stiahnuť z TiDo"}
            </button>
          )}
          <button onClick={() => setEditOpen(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="edit-store-button">
            <Gear size={16} /> Konfigurácia
          </button>
        </div>
      </div>

      {hasData && (
        <div className="card-flat p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="label-eyebrow block mb-1">Obdobie od</label>
            <input type="date" value={from} min={range.min} max={range.max} onChange={(e) => setFrom(e.target.value)} data-testid="period-from-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Do</label>
            <input type="date" value={to} min={range.min} max={range.max} onChange={(e) => setTo(e.target.value)} data-testid="period-to-input" />
          </div>
          <div className="flex-1 text-xs text-[var(--text-muted)] tabular">
            ℹ Dáta dostupné: <span className="text-[var(--text-secondary)]">{range.min} → {range.max}</span> ({range.days} dní, {range.count} záznamov). Zatvorené dni (Ne) sú z analýzy <strong>vylúčené</strong>.
          </div>
          <div className="flex flex-wrap gap-2 ml-auto items-center">
            {(() => {
              const presets = [
                { id: "1m", label: "1 mesiac", days: 30 },
                { id: "3m", label: "Kvartál", days: 90 },
                { id: "6m", label: "Polrok", days: 180 },
                { id: "1y", label: "Rok", days: 365 },
              ];
              const setPreset = (days) => {
                if (!range?.max) return;
                const maxDate = new Date(range.max);
                const fromDate = new Date(maxDate);
                fromDate.setDate(fromDate.getDate() - (days - 1));
                const minDate = new Date(range.min);
                const effectiveFrom = fromDate < minDate ? minDate : fromDate;
                setFrom(effectiveFrom.toISOString().slice(0, 10));
                setTo(range.max);
              };
              return presets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPreset(p.days)}
                  className="btn-ghost text-xs"
                  data-testid={`period-preset-${p.id}`}
                  title={`Posledných ${p.days} dní`}
                >
                  {p.label}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={() => {
                setFrom(range.min);
                setTo(range.max);
              }}
              className="btn-ghost text-xs"
              data-testid="period-all-button"
            >
              Celé obdobie
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const qs = `period_from=${from || ""}&period_to=${to || ""}`;
                  await downloadFile(`/stores/${storeId}/export.xlsx?${qs}`, `${store?.code || "stredisko"}_export.xlsx`);
                } catch (e) {
                  console.error("Export failed", e);
                  alert("Export sa nepodaril. Skontrolujte konzolu prehliadača.");
                }
              }}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="export-xlsx-button"
            >
              <DownloadSimple size={14} /> Excel export
            </button>
          </div>
        </div>
      )}

      <div className="border-b border-[var(--border-default)] mb-6 overflow-x-auto scroll-area">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[var(--brand)] text-[var(--text-primary)]"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {t.label}
              {t.id === "highlights" && highlightsList.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-[var(--brand)] text-white px-1.5 tabular">
                  {highlightsList.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        {tab === "overview" && (hasData ? <Overview storeId={storeId} period={period} store={store} /> : <EmptyState onOpenConfig={() => setEditOpen(true)} />)}
        {tab === "heatmap" && (hasData ? <HeatmapCalendar storeId={storeId} period={period} /> : <EmptyState />)}
        {tab === "weekday" && (hasData ? <WeekdayCompare storeId={storeId} period={period} store={store} /> : <EmptyState />)}
        {tab === "hourly" && (hasData ? <HourlyBreakdown storeId={storeId} period={period} store={store} /> : <EmptyState />)}
        {tab === "customers" && (hasData ? <PurchasesByHour storeId={storeId} period={period} /> : <EmptyState />)}
        {tab === "efficiency" && (hasData ? <Efficiency storeId={storeId} period={period} /> : <EmptyState />)}
        {tab === "anomalies" && (hasData ? <Anomalies storeId={storeId} period={period} /> : <EmptyState />)}
        {tab === "compare" && (hasData ? <PeriodCompare storeId={storeId} range={range} /> : <EmptyState />)}
        {tab === "forecast" && (hasData ? (
          <div className="space-y-6">
            <Forecast storeId={storeId} range={range} period={period} />
            <details className="card-flat" data-testid="simulation-collapsible">
              <summary className="px-5 py-4 cursor-pointer font-display text-base font-semibold flex items-center gap-2 hover:bg-[var(--bg-surface-hover)]">
                <span>Simulácia „čo keby…“</span>
                <span className="text-xs text-[var(--text-muted)] font-normal">
                  → overte si dopad zmien do plánu personálu
                </span>
              </summary>
              <div className="px-5 pb-5 border-t border-[var(--border-default)] pt-5">
                <WhatIf storeId={storeId} range={range} period={period} />
              </div>
            </details>
          </div>
        ) : <EmptyState />)}
        {tab === "predikcia" && (hasData ? <Predikcia storeId={storeId} /> : <EmptyState />)}
        {tab === "summary" && (
          <div className="space-y-6" data-testid="summary-tab">
            <AiReport storeId={storeId} period={period} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-display text-base font-semibold mb-3">Označené body</h3>
                <Highlights storeId={storeId} />
              </div>
              <div>
                <h3 className="font-display text-base font-semibold mb-3">Poznámky</h3>
                <Notes storeId={storeId} />
              </div>
            </div>
          </div>
        )}
      </div>

      {editOpen && <StoreFormDialog store={store} onClose={() => setEditOpen(false)} />}
    </div>
  );
}

function EmptyState({ onOpenConfig }) {
  return (
    <div className="card-flat p-10 text-center space-y-3">
      <p className="text-[var(--text-secondary)]">Zatiaľ tu nie sú žiadne dáta.</p>
      <p className="text-xs text-[var(--text-muted)]">
        Použite tlačidlo <strong>Znovu stiahnuť z TiDo</strong> hore, alebo{" "}
        {onOpenConfig ? (
          <button className="link" onClick={onOpenConfig} data-testid="empty-open-config">
            otvorte Konfiguráciu
          </button>
        ) : (
          <>otvorte Konfiguráciu</>
        )}{" "}a importuj CSV manuálne.
      </p>
    </div>
  );
}
